import { Avatar, Tooltip, Button, Space, Popconfirm } from 'antd';
import { NavLink } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, CalendarOutlined, CopyOutlined } from '@ant-design/icons';
import Moment from 'react-moment';
import styles from './TacheBacklogItem.module.scss';
import PropTypes from 'prop-types';
import { useUserContext } from '../../contexts/UserContext';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { tachesState } from '../../state/actions/tachesState';
import { directoriesState } from '../../state/actions/directoriesState';
import { useEffect, useState } from 'react';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import { TacheService } from '../../services/TacheService';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { isTacheLocked } from '../../helpers/permissions';
import TacheEditForm from './TacheEditForm';

const TacheBacklogItem = ({ tache, showAffaire = true, onItemClick, onUpdate }) => {
  const { check, currentUser } = useUserContext();
  const canManage = (tache?.user_id === currentUser?.id ||
    tache?.responsable_id === currentUser?.personnel?.id ||
    tache?.affaire?.user_id === currentUser?.id ||
    tache?.affaire?.responsable_id === currentUser?.id) && !isTacheLocked(tache);
  const [editVisible, setEditVisible] = useState(false);
  const personnels = useSelector(() => state$.personnelsDirectory.list.get());

  useEffect(() => { directoriesState.loadPersonnels(); }, []);

  const handleFieldChange = (value, fieldName, record) => {
    TacheService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => {
        tachesState.updateTacheInList(r.data);
        onUpdate?.(r.data);
      })
      .catch(catchError);
  };

  const priorityColor = tache.priorite?.couleur ?? '#ccc';
  const isResolved = tache.statut?.resolu;

  const handleNameClick = (e) => {
    if (onItemClick) {
      e.preventDefault();
      onItemClick(tache.uid);
    }
  };

  return (
    <div className={styles.item}>
      <div className={styles.priorityStripe} style={{ backgroundColor: priorityColor }} />

      <div className={styles.content}>
        {/* Left: single row — code + name, then etape/statut below */}
        <div className={styles.nameBlock}>
          <div className={styles.nameRow}>
            <Tooltip title="Copier le lien de la tâche">
              <span
                className={styles.codeTag}
                onClick={() => copyLinkToClipboard(`/tache/${tache.uid}`, 'Lien de la tâche')}
                style={{ cursor: 'pointer' }}
              >
                {tache.code} <CopyOutlined style={{ fontSize: 10 }} />
              </span>
            </Tooltip>
            <InlineEditableInput
              canEdit={canManage}
              type="text"
              fieldName="nom"
              value={tache.nom}
              record={tache}
              onChange={handleFieldChange}
            >
              {check('VIEW-TACHE')
                ? <NavLink to={`/tache/${tache.uid}`} className={`${styles.nameLink} ${isResolved ? styles.resolved : ''}`} onClick={handleNameClick}>{tache.nom}</NavLink>
                : <span className={`${styles.nameLink} ${isResolved ? styles.resolved : ''}`}>{tache.nom}</span>
              }
            </InlineEditableInput>
            {tache.statut && (
              <span className={styles.statutChip} style={{ borderColor: tache.statut.couleur, color: tache.statut.couleur }}>
                {tache.statut.nom}
              </span>
            )}
          </div>
          {/* <div className={styles.subRow}>
            {tache.affaire?.etape && (
              <span className={styles.etapeChip}>{tache.affaire.etape.nom}</span>
            )}
          </div> */}
        </div>

        {/* Dossier — compact chip, truncated */}
        {showAffaire && tache.affaire && (
          <Tooltip title={tache.affaire.nom}>
            {check('VIEW-AFFAIRE')
              ? <NavLink to={`/affaire/${tache.affaire.uid}`} className={styles.affaireChip}>{tache.affaire.code ?? tache.affaire.nom}</NavLink>
              : <span className={styles.affaireChip}>{tache.affaire.code ?? tache.affaire.nom}</span>
            }
          </Tooltip>
        )}

        {/* Priority tag */}
        {tache.priorite?.nom && (
          <span className={styles.priorityTag} style={{ background: priorityColor }}>
            {tache.priorite.nom}
          </span>
        )}
      </div>

      <div className={styles.meta}>
        {tache.date_limite && (
          <Tooltip title="Date limite">
            <span className={`${styles.date} ${tache.retard ? styles.dateRetard : ''}`}>
              <CalendarOutlined />
              <Moment format="DD/MM/YY">{tache.date_limite}</Moment>
            </span>
          </Tooltip>
        )}
        <InlineEditableInput
          canEdit={canManage}
          clickToEdit
          type="select"
          fieldName="responsable_id"
          value={tache.responsable_id}
          record={tache}
          onChange={handleFieldChange}
          options={personnels.map(p => ({
            value: p.id,
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar size={20} src={p.avatar_link}>{p.name?.[0]}</Avatar>
                {p.name}
              </span>
            ),
            children: p.name,
          }))}
        >
          {tache.responsable && (
            <Tooltip title={tache.responsable.name}>
              <Avatar size={24} src={tache.responsable.avatar_link}>
                {tache.responsable.name?.[0]}
              </Avatar>
            </Tooltip>
          )}
        </InlineEditableInput>
        <Space size={2}>
          {canManage && (
            <Tooltip title="Modifier">
              <Button type="text" size="small" icon={<EditOutlined />} className={styles.editBtn} onClick={() => setEditVisible(true)} />
            </Tooltip>
          )}
          {canManage && (
            <Tooltip title="Supprimer">
              <Popconfirm title="Supprimer cette tâche ?" okType="danger" okText="Supprimer" cancelText="Annuler" onConfirm={() => tachesState.deleteTache(tache.uid)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      </div>

      {editVisible && (
        <TacheEditForm
          visible={editVisible}
          uid={tache.uid}
          onUpdate={(updated) => {
            setEditVisible(false);
            tachesState.updateTacheInList(updated);
            onUpdate?.(updated);
          }}
          onCancel={() => setEditVisible(false)}
        />
      )}
    </div>
  );
};

TacheBacklogItem.propTypes = { tache: PropTypes.object.isRequired, showAffaire: PropTypes.bool, onItemClick: PropTypes.func, onUpdate: PropTypes.func };
export default TacheBacklogItem;
